//@flow
import FunctionStore from '../Base/functionStoreClass';
import { setFlag, apiUpdateState, updateState, setFlagProp } from '../Base/storeUtils';
import Constants from '../../constants';
import ApiInterface from '../../APIInterfaces/customer';
import SessionStore from '../Session/session';
import CookiesData from '../../Utils/cookies-data';
import Customer from './customerModel';
import { toImmutable } from '../../Utils/immutable';
import AddressesCollection from '../Addresses/addressesCollection';
import { FriendBuyVisitorModel } from '../../Utils/friendbuyV2';

import type AddressesModel, { AddressFields, AddressObject } from '../Addresses/addressModel';
import type { HandlerAdd, StoreState } from '../Base/functionStoreClass';
import type APIResponseReader from '../../Utils/api-response-reader';
import type QuoteModel from '../Quote/quoteModel';
// set _validationError or _emailValidationError flag for email and password editors
// * these are used by the account email/password edit modal to track
//   the request status

export default class CustomerStore extends FunctionStore {
  isExistingUserEmail():boolean {
    return this._getFlag('existingUserEmail');
  }

  isGhostAccountEmail():boolean {
    return this._getFlag('ghostAccountEmail');
  }

  getResetEmailNotice():boolean {
    return this._getFlag('resetEmailNotice');
  }

  getResetPassword():boolean {
    return this._getFlag('resetPassword');
  }

  getMagicLinkNotice():boolean {
    return this._getFlag('magicLinkNotice');
  }

  getCustomer():Customer {
    return this._resolvePropertyObject('customer', Customer);
  }

  getResetEmail():?string {
    return this.getState().get('resetEmail', null);
  }

  getMagicLinkEmail():?string {
    return this.getState().get('magicLinkEmail', null);
  }

  getCustomerEmail():?string {
    const cookieEmail = CookiesData.getEmail();
    return cookieEmail && cookieEmail.email;
  }

  emailModalIsOpen():boolean {
    return this._getFlag('emailModalIsOpen');
  }

  passwordModalIsOpen():boolean {
    return this._getFlag('passwordModalIsOpen');
  }

  getValidationError():boolean {
    return this._getFlag('validationError');
  }

  getEmailValidationError():boolean {
    return this._getFlag('emailValidationError');
  }

  getGiftcardAmount():?number {
    return this.getState().get('giftCardAmount');
  }

  getAddresses():AddressesCollection {
    return this._resolvePropertyObject(['customer', 'addresses'], AddressesCollection);
  }

  inUS(quote:QuoteModel):boolean {
    const currentCountry = this.getCurrentCountryCode(quote);
    return !currentCountry || currentCountry === 'US';
  }

  getCurrentAddress(quote:QuoteModel):?AddressesModel {
    const addresses = this.getAddresses();
    if(!quote || !quote.getShippingAddressId() || !addresses) return undefined;

    return addresses.findById(quote.getShippingAddressId());
  }

  getCurrentCountryCode(quote:QuoteModel):string {
    const currentAddress = this.getCurrentAddress(quote);
    if (currentAddress) return currentAddress.getCountryId();
    return quote.getIpCountry();
  }

  getEmailDomainValidationError():?string {
    return this.getState().get('emailDomainValidationError');
  }

  getFriendBuyVisitor():FriendBuyVisitorModel {
    return this._resolvePropertyObject('friendbuyData', FriendBuyVisitorModel);
  }

  _radianceRequestMade:?boolean

  _setup(onSuccess:HandlerAdd, onError:HandlerAdd) {
    onSuccess(Constants.LOGIN, ApiInterface.getCustomer, [ SessionStore ]);
    onSuccess(Constants.LOGGED_IN, ApiInterface.getCustomer, [ SessionStore ]);
    onSuccess(Constants.LOGOUT, updateState(_clearCustomer));
    onSuccess(Constants.CREATE_CUSTOMER, updateState(_createCustomer));
    onSuccess(Constants.CREATE_CUSTOMER_SUCCESS, [
      _customerCreated,
      apiUpdateState(_customerLoadSuccessful),
    ]);
    onSuccess(Constants.SEND_PASSWORD_RESET_EMAIL, updateState(_sendPasswordResetEmail));
    onSuccess(Constants.REMOVE_RESET_EMAIL_NOTICE, setFlag('resetEmailNotice', false));
    onSuccess(Constants.RESET_PASSWORD_SUCCESS, setFlag('resetPassword', true));
    onSuccess(Constants.GET_CUSTOMER_SUCCESS, apiUpdateState(_customerLoadSuccessful));
    onSuccess(Constants.OPEN_EMAIL_MODAL, setFlag('emailModalIsOpen', true));
    onSuccess(Constants.CLOSE_EMAIL_MODAL, updateState(_closeEmailModal));
    onSuccess(Constants.OPEN_PASSWORD_MODAL, setFlag('passwordModalIsOpen', true));
    onSuccess(Constants.CLOSE_PASSWORD_MODAL, updateState(_closePasswordModal));
    onSuccess(Constants.REMOVE_CUSTOMER_EMAIL, () => CookiesData.remove('email'));
    onSuccess(Constants.UPDATE_CUSTOMER_DETAILS, _updateCustomerDetails);
    onSuccess(Constants.UPDATE_CUSTOMER_DETAILS_SUCCESS, apiUpdateState(_updateCustomerDetailsSuccess));
    onSuccess(Constants.REMOVE_EXISTING_EMAIL_ERROR, setFlag('existingUserEmail', false));
    onSuccess(Constants.SEND_MAGIC_LINK, updateState(_sendMagicLink));
    onSuccess(Constants.REMOVE_MAGIC_LINK_NOTICE, setFlag('magicLinkNotice', false));
    onSuccess(Constants.REDEEM_GIFTCODE_SUCCESS, apiUpdateState(_redeemGiftcodeSuccess));
    onSuccess(Constants.UPDATE_QUOTE_WITH_AMAZON_SUCCESS, ApiInterface.getCustomer);
    onSuccess(Constants.ADD_ADDRESS, _addAddress);
    onSuccess(Constants.DELETE_ADDRESS, _deleteAddress);
    onSuccess(Constants.UPDATE_ADDRESS, _updateAddress);
    onSuccess(Constants.SET_EMAIL_DOMAIN_VALIDATION_ERROR, updateState(_setEmailDomainValidationError));
    onSuccess(Constants.CLOSE_MODAL, updateState(_closeModal));
    onSuccess(Constants.SET_FRIENDBUY_DATA, updateState(_setFriendBuyData));

    onError(Constants.CREATE_CUSTOMER_SUCCESS, updateState(_customerCreationError));
    onError(Constants.UPDATE_CUSTOMER_DETAILS_SUCCESS, updateState(_updateCustomerDetailsError));
    onError(Constants.LOGIN, updateState(_customerLoginError));
  }
}

function _addAddress(store:CustomerStore, data:{addressInfo:AddressFields}) {
  const customerId = store.getCustomer().getId();
  ApiInterface.addAddress(customerId, data.addressInfo);
}

function _updateAddress(store:CustomerStore, data:{addressId:string, addressInfo:$Shape<AddressObject>}) {
  const customerId = store.getCustomer().getId();
  ApiInterface.updateAddress(customerId, data.addressId, data.addressInfo);
}

function _deleteAddress(store:CustomerStore, data:{addressId:string}) {
  const customerId = store.getCustomer().getId();
  ApiInterface.deleteAddress(customerId, data.addressId);
}

function _clearCustomer(state:StoreState) {
  state.delete('customer');
}

function _createCustomer(state:StoreState, data:Object) {
  ApiInterface.createCustomer(data);
}

function _customerCreated() {
  CookiesData.set('hideExitPop', true);
}

function _sendPasswordResetEmail(state:StoreState, data:{ email:string }) {
  setFlagProp(state, 'resetEmailNotice', true);
  state.set('resetEmail', data.email);
  ApiInterface.sendPasswordResetEmail(data.email);
}

function _sendMagicLink(state:StoreState, data:{ email:string }) {
  setFlagProp(state, 'magicLinkNotice', true);
  state.set('magicLinkEmail', data.email);
  ApiInterface.sendMagicLink(data.email);
}

function _redeemGiftcodeSuccess(state:StoreState, data:Object) {
  state.set('giftCardAmount', data.store_credit);
  ApiInterface.getCustomer();
}

function _customerLoadSuccessful(state:StoreState, data:Object) {
  state.set('customer', toImmutable(data.customer || {}));
  state.setIn(['customer', 'subscription'], toImmutable(data.subscription));
  state.setIn(['customer', 'membership'], toImmutable(data.membership));
  state.setIn(['customer', 'addresses'], toImmutable(data.addresses));
}

function _closeEmailModal(state:StoreState) {
  setFlagProp(state, 'emailModalIsOpen', false);
  setFlagProp(state, 'validationError', false);
  setFlagProp(state, 'emailValidationError', false);
}

function _closePasswordModal(state:StoreState) {
  setFlagProp(state, 'passwordModalIsOpen', false);
  setFlagProp(state, 'validationError', false);
  setFlagProp(state, 'emailValidationError', false);
}

function _setEmailDomainValidationError(state:StoreState, data:Object) {
  state.set('emailDomainValidationError', data.error);
}

function _setFriendBuyData(state:StoreState, data:Object) {
  state.set('friendbuyData', toImmutable(data));
}

function _closeModal(state:StoreState, modalId:string) {
  // Clear emailDomainValidationError whenever login
  // modal closes
  if (modalId === Constants.LOGIN_MODAL) {
    state.set('emailDomainValidationError', '');
  }
}

function _updateCustomerDetails(store:CustomerStore, data:Object) {
  ApiInterface.updateCustomerDetails(store.getCustomer().getId(), data);
}

// update the customer's email/password/is_guest after doing a PUT to the customers end-point
// * this will create a new Customer model instance with the updated
//   email/password/is_guest attributes
function _updateCustomerDetailsSuccess(state:StoreState, data:Object) {
  _customerLoadSuccessful(state, data);
  _closePasswordModal(state);
  _closeEmailModal(state);
}

function _customerCreationError(state:StoreState, response:APIResponseReader) {
  switch (response.getErrors()[0]) {
    case 'This customer email already exists':
      setFlagProp(state, 'existingUserEmail', true);
      break;
    case 'Customer account is marked as is_guest':
      setFlagProp(state, 'ghostAccountEmail', true);
      break;
  }
}

function _customerLoginError(state:StoreState, response:APIResponseReader) {
  switch (response.getErrors()[0]) {
    case 'customer account is marked as is_guest':
      setFlagProp(state, 'ghostAccountEmail', true);
      break;
  }
}

function _updateCustomerDetailsError(state:StoreState, response:APIResponseReader) {
  // an error occurred while updating the email or password
  const requestData = response.getRequestData();
  if (requestData.hasOwnProperty('new_password') && requestData.hasOwnProperty('confirmation')) {
    setFlagProp(state, 'passwordModalIsOpen', true);
  } else {
    setFlagProp(state, 'emailModalIsOpen', true);
  }

  switch (response.getErrors()[0]) {
    case 'This customer email already exists':
    case 'new email exists and is marked as is_guest':
      setFlagProp(state, 'existingUserEmail', true);
      setFlagProp(state, 'emailValidationError', true);
      setFlagProp(state, 'validationError', false);
      break;
    case 'Incorrect original password':
      setFlagProp(state, 'validationError', true);
      setFlagProp(state, 'emailValidationError', false);
      break;
  }
}
